import { RefObject, useLayoutEffect } from "react";
import { gsap } from "./gsap";

type RevealVariant = "up" | "down" | "left" | "right" | "scale";

const getRevealFrom = (variant: RevealVariant, distance: number) => {
    switch (variant) {
        case "left":
            return { x: -distance, opacity: 0 };
        case "right":
            return { x: distance, opacity: 0 };
        case "down":
            return { y: -distance, opacity: 0 };
        case "scale":
            return { scale: 0.96, opacity: 0 };
        case "up":
        default:
            return { y: distance, opacity: 0 };
    }
};

const useGsapReveal = (scopeRef: RefObject<HTMLElement>) => {
    useLayoutEffect(() => {
        const scope = scopeRef.current;
        if (!scope) return;

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        const context = gsap.context(() => {
            const elements = gsap.utils.toArray<HTMLElement>("[data-reveal]", scope);

            elements.forEach((element) => {
                const variant = (element.dataset.reveal || "up") as RevealVariant;
                const distance = Number(element.dataset.revealDistance || 24);

                gsap.fromTo(
                    element,
                    {
                        ...getRevealFrom(variant, distance),
                        willChange: "transform, opacity",
                    },
                    {
                        x: 0,
                        y: 0,
                        scale: 1,
                        opacity: 1,
                        duration: 0.9,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: element,
                            start: "top 85%",
                            toggleActions: "play none none reverse",
                        },
                    }
                );
            });
        }, scope);

        return () => context.revert();
    }, [scopeRef]);
};

export default useGsapReveal;
